import { useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";

/**
 * TarotCard: face-down back, or drawn card face with vintage RWS illustration.
 * Always upright. Props documented inline.
 */
export const TarotCard = ({
  card = null,
  revealed = false,
  size = "md",
  onClick,
  selectable = false,
  selected = false,
  testId,
  label = "",
  index = 0,
  glow = false,
}) => {
  const { language } = useLanguage();
  const [imgError, setImgError] = useState(false);

  const dims = {
    sm: "w-16 h-24 md:w-20 md:h-32",
    md: "w-24 h-40 md:w-28 md:h-44",
    lg: "w-32 h-52 md:w-40 md:h-64",
  }[size];

  const displayName = card ? (language === "fr" ? card.name_fr : card.name_en) : "";
  const showImage = card && card.image && !imgError;

  return (
    <div className="flex flex-col items-center gap-2">
      {label && (
        <span className="text-[0.72rem] md:text-[0.82rem] tracking-[0.28em] uppercase text-[#14103A] font-bold font-sans text-center">
          {label}
        </span>
      )}
      <motion.button
        type="button"
        onClick={onClick}
        data-testid={testId}
        whileHover={selectable && !revealed ? { y: -6 } : {}}
        whileTap={selectable && !revealed ? { scale: 0.97 } : {}}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        className={`relative ${dims} perspective-1200 focus:outline-none ${
          selectable ? "cursor-pointer" : "cursor-default"
        } ${glow ? "drop-shadow-[0_0_20px_rgba(212,175,55,0.35)]" : ""}`}
        aria-label={displayName || "tarot card"}
      >
        <motion.div
          className="relative w-full h-full preserve-3d"
          initial={false}
          animate={{ rotateY: revealed ? 180 : 0 }}
          transition={{ duration: 0.8, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* BACK */}
          <div
            className={`absolute inset-0 backface-hidden card-back rounded-md border border-gold/30 shadow-[0_12px_30px_-12px_rgba(20,16,58,0.35)] ${
              selected ? "ring-2 ring-gold ring-offset-2 ring-offset-[#F4EAD1]" : ""
            }`}
          >
            <div className="absolute inset-2 border border-gold/25 rounded-sm flex items-center justify-center">
              <div className="rune text-2xl md:text-3xl opacity-70">✶</div>
            </div>
            <div className="absolute inset-4 border border-gold/15 rounded-sm" />
          </div>

          {/* FACE — vintage RWS illustration when available */}
          <div
            className="absolute inset-0 backface-hidden card-face rounded-md border border-gold/40 shadow-[0_12px_30px_-12px_rgba(20,16,58,0.35)] overflow-hidden flex flex-col"
            style={{ transform: "rotateY(180deg)" }}
          >
            {showImage ? (
              <>
                <div className="relative flex-1 overflow-hidden bg-[#F4EAD1]">
                  <img
                    src={card.image}
                    alt={displayName}
                    onError={() => setImgError(true)}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                </div>
                <div className="w-full text-center px-1.5 py-1 bg-oxblood-surface/95 border-t border-gold/40">
                  <div className="font-serif text-ivory text-[0.62rem] md:text-[0.72rem] leading-tight">
                    {displayName}
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full p-2 md:p-3 flex flex-col items-center justify-between">
                <div className="w-full flex items-center justify-between">
                  <span className="rune text-sm md:text-lg opacity-80">✧</span>
                  <span className="font-serif italic text-gold/90 text-[0.7rem] md:text-sm">
                    {card?.roman}
                  </span>
                  <span className="rune text-sm md:text-lg opacity-80">✧</span>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="rune text-4xl md:text-5xl opacity-85 leading-none">
                    {card?.suit_glyph || "☉"}
                  </div>
                </div>
                <div className="w-full text-center">
                  <div className="gold-hairline mb-1.5" />
                  <div className="font-serif text-ivory text-[0.68rem] md:text-[0.8rem] leading-tight">
                    {displayName}
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.button>
    </div>
  );
};

export default TarotCard;
